import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ROUTES } from "~/router/constants";
import { Title } from "~/shared";
import HighlightedText from "~/ui/common/HighlightedText";
import { tw } from "~/utils";
import { FAQItem, FAQText } from "./";

export const FAQDesktop = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState<string>("About Elektra");
  const contentRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScroll = useRef<boolean>(false);

  const toggleItem = (index: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const detectVisibleSection = () => {
    if (!contentRef.current || isProgrammaticScroll.current) return;

    const container = contentRef.current;
    const containerRect = container.getBoundingClientRect();
    const sections = Object.keys(FAQText);

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isAtBottom) {
      const lastSection = sections[sections.length - 1];
      setActiveSection(lastSection);
      return;
    }

    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const sectionTitle = sections[sectionIndex];
      const element = document.getElementById(
        sectionTitle.replace(/\s+/g, "-").toLowerCase(),
      );

      if (element) {
        const elementRect = element.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;

        const isVisible =
          elementRect.top < containerBottom &&
          elementRect.bottom > containerTop;

        if (isVisible) {
          setActiveSection(sectionTitle);
          break;
        }
      }
    }
  };

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!isProgrammaticScroll.current) {
        detectVisibleSection();
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToSection = (sectionTitle: string) => {
    isProgrammaticScroll.current = true;

    setActiveSection(sectionTitle);

    const element = document.getElementById(
      sectionTitle.replace(/\s+/g, "-").toLowerCase(),
    );
    if (element) {
      const contentContainer = document.querySelector(".faq-content");
      if (contentContainer) {
        const handleScrollEnd = () => {
          isProgrammaticScroll.current = false;
          contentContainer.removeEventListener("scrollend", handleScrollEnd);
        };

        const fallbackTimer = setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 500);

        if ("onscrollend" in contentContainer) {
          contentContainer.addEventListener("scrollend", handleScrollEnd);
          clearTimeout(fallbackTimer);
        }

        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }
    }
  };

  return (
    <section className="hidden sm:block">
      <div className="font-sans">
        <header className="mb-8">
          <Title>
            <HighlightedText>Frequently asked questions</HighlightedText>
          </Title>
        </header>

        <div className="flex gap-8">
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-6">
              <nav className="space-y-3">
                {Object.keys(FAQText).map((sectionTitle) => (
                  <div key={sectionTitle} className="relative">
                    <button
                      onClick={() => scrollToSection(sectionTitle)}
                      className={tw(
                        "w-full cursor-pointer text-left font-sans font-medium transition-colors",
                        activeSection === sectionTitle
                          ? "text-violet pl-2"
                          : "text-indigo hover:text-gray-900",
                      )}
                    >
                      {activeSection === sectionTitle && (
                        <div className="bg-violet absolute bottom-0 left-0 top-0 w-1 rounded"></div>
                      )}
                      {sectionTitle}
                    </button>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          <div
            ref={contentRef}
            className="faq-content max-h-[calc(100vh-250px)] flex-1 overflow-y-auto pb-40"
          >
            {Object.entries(FAQText).map(
              ([sectionTitle, items], sectionIndex) => (
                <div
                  key={sectionIndex}
                  id={sectionTitle.replace(/\s+/g, "-").toLowerCase()}
                  className="mb-24"
                >
                  <h2 className="font-ivy text-indigo mb-6 text-2xl font-medium tracking-normal">
                    {sectionTitle}
                  </h2>
                  <div className="border-neutral border-t bg-white">
                    {items.map((item, itemIndex) => {
                      const globalIndex = `${sectionIndex}-${itemIndex}`;
                      return (
                        <FAQItem
                          key={globalIndex}
                          question={item.question}
                          answer={item.answer}
                          isOpen={!!openItems[globalIndex]}
                          onToggle={() => toggleItem(globalIndex)}
                        />
                      );
                    })}
                  </div>
                </div>
              ),
            )}

            <div>
              <p className="text-indigo font-sans text-sm">
                Have a question we didn’t answer?{" "}
                <Link
                  to={ROUTES.hubContactUs}
                  className="text-violet font-medium"
                >
                  Contact us!
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
